/**
 * Repositorio de dispositivos: LA FRONTERA DE MIGRACION.
 *
 * La interfaz nunca llama a `api.js` ni al mock directamente. Llama aqui, y
 * este archivo decide de donde salen los datos segun QUIEN haya iniciado sesion:
 * el usuario de demostracion ve datos simulados, cualquier otro ve los reales
 * de su dispositivo vinculado.
 *
 * Lo que este archivo garantiza a quien lo consume:
 *   - Siempre devuelve modelos normalizados (`normalizeDevice`), nunca crudos.
 *   - Siempre lanza `ApiError` con un `code` de ERROR_CODE, nunca cadenas sueltas.
 *
 * Ese contrato es lo que permite que los componentes escritos hoy contra mocks
 * sigan funcionando manana contra la API sin tocarlos.
 */

import { mockApi } from '../mock/mockBackend';
import { ApiError, ERROR_CODE } from '../apiError';
import { normalizeDevice, aggregateDevices, valveStateOf } from '../../domain/device';
import { isDemoSession } from '../demoSession';
import api from '../api';

/* Nota: la identidad del usuario NO se envía desde el cliente. La Lambda la
   toma del token que valida API Gateway, así que las rutas no llevan el id. */

/**
 * Interruptor de fuente de datos, decidido POR SESION y no en tiempo de compilacion.
 *
 * - Sesion de demostracion  -> datos simulados.
 * - Cualquier otra sesion   -> datos reales del dispositivo vinculado.
 *
 * Se consulta en cada llamada, no una sola vez al cargar el modulo: si se
 * evaluara al importar, un usuario que cierra la demo y entra con su cuenta
 * real seguiria viendo datos simulados hasta recargar la pagina.
 */
const usarSimulacion = () => isDemoSession();

/** Traduce un fallo de axios a nuestro contrato de errores. */
function traducirError(err) {
  if (err instanceof ApiError) return err;
  const status = err?.response?.status;
  const codigoServidor = err?.response?.data?.code;
  const mensajeServidor = err?.response?.data?.message ?? err?.response?.data?.error;
  if (codigoServidor && Object.values(ERROR_CODE).includes(codigoServidor)) {
    return new ApiError(codigoServidor, mensajeServidor || err.message);
  }
  if (status === 404) return new ApiError(ERROR_CODE.NOT_FOUND, 'Recurso no encontrado');
  if (status === 409) return new ApiError(ERROR_CODE.ALREADY_LINKED_BY_OTHER, mensajeServidor || 'La operación entró en conflicto con el estado actual');
  if (status === 403) return new ApiError(ERROR_CODE.LIMIT_REACHED, mensajeServidor || 'Operación no permitida');
  return new ApiError(ERROR_CODE.NETWORK, mensajeServidor || err?.message || 'Error de conexión');
}

export const deviceRepository = {
  /**
   * Dispositivos accesibles por el usuario autenticado.
   *
   * El endpoint correcto es `GET /devices`: la Lambda toma el `user_id` del
   * token y filtra por `device_permissions`, de modo que cada usuario recibe
   * solo los suyos.
   *
   * NO usar `users/{id}/devices`: esa ruta existe en API Gateway pero la Lambda
   * no la atiende y cae en su 404. Es la que usaba `deviceService` y por eso la
   * lista nunca llegaba a cargar.
   */
  async list() {
    try {
      if (usarSimulacion()) {
        return (await mockApi.listDevices()).map(normalizeDevice);
      }
      const { data } = await api.get('devices');
      const lista = data.devices ?? data.items ?? (Array.isArray(data) ? data : []);
      return lista.map(normalizeDevice);
    } catch (err) {
      throw traducirError(err);
    }
  },

  /**
   * Un dispositivo concreto.
   *
   * NO se llama a `GET /devices/{id}`: esa ruta no la atiende la Lambda -su
   * enrutador solo reconoce las subrutas (/telemetry, /valve, /config...) y el
   * `/devices` a secas- y cae en su 404 generico. Se resuelve desde la lista,
   * que si existe y ya viene filtrada por permisos del token.
   */
  async get(deviceId) {
    try {
      if (usarSimulacion()) return normalizeDevice(await mockApi.getDevice(deviceId));
      const encontrado = (await this.list()).find((d) => d.device_id === deviceId);
      if (!encontrado) throw new ApiError(ERROR_CODE.NOT_FOUND, 'Dispositivo no encontrado');
      return encontrado;
    } catch (err) {
      throw traducirError(err);
    }
  },

  /**
   * Vincula un dispositivo a la cuenta.
   * @param {{serial: string, code: string}} credenciales
   *
   * PUNTO DE MIGRACION: el endpoint real sera `POST /account/devices` con
   * `{serial, activation_code}` en el cuerpo. La identidad del usuario NO se
   * envia: sale del token de Cognito que valida API Gateway. Mandarla desde el
   * cliente permitiria vincular dispositivos a cuentas ajenas.
   */
  async link(credenciales) {
    try {
      if (usarSimulacion()) {
        return normalizeDevice(await mockApi.linkDevice(credenciales));
      }
      // El backend NO tiene todavia autoservicio de vinculacion: solo existe
      // `POST /users/{id}/devices`, que es de administracion y recibe un
      // device_id ya conocido, no un numero de serie con su codigo. Se falla de
      // forma explicita en lugar de llamar a una ruta que no hace lo que se
      // espera. Ver PLAN-BACKEND-DISPOSITIVOS.md, apartado 3.1.
      throw new ApiError(
        ERROR_CODE.NETWORK,
        'La vinculación desde la aplicación aún no está disponible. Escríbenos para dar de alta tu dispositivo.'
      );
    } catch (err) {
      throw traducirError(err);
    }
  },

  async unlink(deviceId) {
    try {
      if (usarSimulacion()) return await mockApi.unlinkDevice(deviceId);
      // No existe ningun endpoint de desvinculacion en el backend desplegado:
      // API Gateway solo expone POST /users/{id}/devices para dar de alta, y ni
      // siquiera esa la atiende la Lambda. Se falla de forma explicita en lugar
      // de simular un exito que no ocurre.
      throw new ApiError(
        ERROR_CODE.NETWORK,
        'La desvinculación aún no está disponible. Escríbenos para dar de baja tu dispositivo.'
      );
    } catch (err) {
      throw traducirError(err);
    }
  },

  /**
   * Control de válvula.
   *
   * En demostración el comando se resuelve contra el estado simulado y NO sale
   * del navegador: nadie debe poder cortar el agua de un dispositivo real desde
   * una cuenta de prueba.
   */
  async controlValve(deviceId, action) {
    try {
      if (action !== 'open' && action !== 'close') {
        throw new ApiError(ERROR_CODE.INVALID_CODE, 'La acción debe ser open o close');
      }
      if (usarSimulacion()) return await mockApi.controlValve(deviceId, action);
      // OJO con lo que significa esta respuesta: la Lambda contesta 200 en
      // cuanto PUBLICA el comando en `NissaGuard/commands/{device_id}`. No
      // espera al dispositivo. Un 200 aqui quiere decir "el mensaje salio", no
      // "la valvula se movio". La confirmacion solo llega en la telemetria
      // siguiente; de eso se encarga `readValveState`.
      return (await api.post(`devices/${deviceId}/valve/control`, { action })).data;
    } catch (err) {
      throw traducirError(err);
    }
  },

  /**
   * Actualiza y mezcla la configuración persistida del dispositivo.
   * `valve_schedules` siempre se envía como lista completa dentro de
   * `configuration`, de modo que crear, editar, activar y eliminar comparten
   * una sola operación atómica.
   */
  async updateConfig(deviceId, changes) {
    try {
      if (usarSimulacion()) return normalizeDevice(await mockApi.updateDeviceConfig(deviceId, changes));
      const { data } = await api.post(`devices/${deviceId}/config`, changes);
      return data;
    } catch (err) {
      throw traducirError(err);
    }
  },

  /**
   * Estado de valvula tal como consta en AWS, con su marca de tiempo.
   *
   * Se lee de la tabla Devices (via `GET /devices`), no de
   * `GET /devices/{id}/valve/status`. La fuente autoritativa es
   * `Devices.reported_valve_state`: sólo la escribe un ACK correlacionado o una
   * lectura física que el firmware marcó como confiable. `valve_state` queda
   * como respaldo de compatibilidad para registros anteriores a la migración.
   *
   * Ademas no cuesta una llamada extra: la lista ya se refresca cada 30 s.
   */
  async readValveState(deviceId) {
    const dispositivo = await this.get(deviceId);
    const reportedState = dispositivo.reported_valve_state
      ?? dispositivo.reportedValveState
      ?? dispositivo.valve_state
      ?? dispositivo.valveState;
    return {
      device_id: deviceId,
      valve_state: valveStateOf(dispositivo),
      reported_state: reportedState,
      reported_state_updated_at: dispositivo.reported_state_updated_at ?? 0,
      reported_state_observed_at: dispositivo.reported_state_observed_at ?? 0,
      reported_state_source: dispositivo.reported_state_source ?? null,
      last_reported_command_id: dispositivo.last_reported_command_id ?? null,
      last_reported_command_timestamp: dispositivo.last_reported_command_timestamp ?? 0,
      last_acknowledged_command_id: dispositivo.last_acknowledged_command_id ?? null,
      last_acknowledged_command_timestamp: dispositivo.last_acknowledged_command_timestamp ?? 0,
      last_acknowledged_command_status: dispositivo.last_acknowledged_command_status ?? null,
      last_seen: dispositivo.last_seen ?? dispositivo.lastSeen ?? 0,
      device: dispositivo,
    };
  },

  /** PUNTO DE MIGRACION: `PATCH /devices/{deviceId}`. */
  async rename(deviceId, cambios) {
    try {
      if (usarSimulacion()) {
        return normalizeDevice(await mockApi.renameDevice(deviceId, cambios));
      }
      // El backend expone la configuracion por POST /devices/{id}/config.
      const { data } = await api.post(`devices/${deviceId}/config`, {
        device_name: cambios.name,
        location: cambios.location,
      });
      return normalizeDevice(data);
    } catch (err) {
      throw traducirError(err);
    }
  },

  /**
   * Resumen agregado de toda la cuenta.
   *
   * TEMPORAL: hoy se calcula en el navegador a partir de la lista completa.
   * Es aceptable con pocos dispositivos y con datos simulados, pero NO escala:
   * obliga a traer la telemetria de cada dispositivo al cliente.
   *
   * PUNTO DE MIGRACION: sustituir por `GET /account/summary`, que agrega en el
   * servidor y devuelve solo el resultado. La firma de esta funcion no cambia,
   * asi que ninguna pantalla se entera.
   */
  async summary() {
    /* No se llama a `account/summary`: ese recurso no existe en API Gateway.
       Pedirlo no daba el 404 que esperaba la degradacion de abajo, sino un
       fallo de CORS -API Gateway responde 403 a una ruta desconocida y el
       preflight nunca devuelve 200-, y un fallo de CORS no trae objeto
       `response`, asi que `err.response.status !== 404` era cierto y la
       funcion acababa lanzando en vez de degradarse.

       Se calcula en cliente, que es lo que se venia haciendo de todos modos.
       Cuando el endpoint exista, se vuelve a llamar aqui y ninguna pantalla se
       entera: la firma no cambia. */
    const devices = await this.list();
    return aggregateDevices(devices);
  },
};

export { ApiError, ERROR_CODE };
export default deviceRepository;
